/**
 * MIGRATE Flow Definition
 * 
 * Fast-path for technical teams migrating existing automation scripts
 * (Selenium/Cypress/Playwright/Robot) into Robitest platform.
 * 
 * Flow: 8 steps total
 * - Steps 1-2: Shared (Intent, Workspace)
 * - Steps 3-6: MIGRATE-specific (Project, Import, Settings, Convert)
 * - Steps 7-8: Reused (Run Test, Summary)
 */

// Import shared step components
import Step1Intent from '../components/wizard/steps/Step1Intent';
import Step2Workspace from '../components/wizard/steps/Step2Workspace';

// Import MIGRATE-specific steps
import StepM3ProjectSetup from '../components/wizard/steps/migrate/StepM3ProjectSetup';
import StepM4ImportScripts from '../components/wizard/steps/migrate/StepM4ImportScripts';
import StepM5ConversionSettings from '../components/wizard/steps/migrate/StepM5ConversionSettings';
import StepM6ConvertProgress from '../components/wizard/steps/migrate/StepM6ConvertProgress';

// Import reused steps
import Step8RunTest from '../components/wizard/steps/Step8RunTest';
import Step9Summary from '../components/wizard/steps/Step9Summary';

/**
 * MIGRATE Flow Definition
 */
const migrateFlow = {
  flowId: 'migrate_scripts',
  intent: 'MIGRATE_SCRIPTS',
  totalSteps: 8,
  
  /**
   * Step definitions
   * Each step includes: id, label, component, and optional gating logic
   */
  steps: [
    {
      id: 1,
      label: 'Intent',
      component: Step1Intent,
      isPublic: true,
      description: 'Select migration intent'
    },
    {
      id: 2,
      label: 'Workspace',
      component: Step2Workspace,
      description: 'Set up your workspace'
    },
    {
      id: 3,
      label: 'Project Setup',
      component: StepM3ProjectSetup,
      description: 'Configure migration project'
    },
    {
      id: 4,
      label: 'Import Scripts',
      component: StepM4ImportScripts,
      description: 'Upload or connect your automation code'
    },
    {
      id: 5,
      label: 'Settings',
      component: StepM5ConversionSettings,
      description: 'Configure conversion preferences'
    },
    {
      id: 6,
      label: 'Convert',
      component: StepM6ConvertProgress,
      description: 'Convert scripts to Robitest format'
    },
    {
      id: 7,
      label: 'Run Test',
      component: Step8RunTest,
      description: 'Execute your migrated tests'
    },
    {
      id: 8,
      label: 'Summary',
      component: Step9Summary,
      description: 'View results and next steps'
    }
  ],
  
  /**
   * Get step component by step number
   * @param {number} stepNum - Step number (1-8)
   * @param {Object} context - Current onboarding context
   * @returns {React.Component|null} Step component
   */
  getStepComponent(stepNum, context) {
    const step = this.steps.find(s => s.id === stepNum);
    return step ? step.component : null;
  },
  
  /**
   * Get next step number
   * @param {number} currentStep - Current step number
   * @param {Object} context - Current onboarding context
   * @returns {number} Next step number
   */
  getNextStep(currentStep, context) {
    // Simple sequential navigation for MIGRATE flow
    const nextStep = currentStep + 1;
    return nextStep <= this.totalSteps ? nextStep : currentStep;
  },
  
  /**
   * Get previous step number
   * @param {number} currentStep - Current step number
   * @param {Object} context - Current onboarding context
   * @returns {number} Previous step number
   */
  getPrevStep(currentStep, context) {
    const prevStep = currentStep - 1;
    return prevStep >= 1 ? prevStep : currentStep;
  },
  
  /**
   * Check if user is allowed to access a specific step
   * Implements gating logic based on completed prerequisites
   * 
   * @param {number} stepNum - Step number to check
   * @param {Object} context - Current onboarding context
   * @returns {boolean} True if step is allowed
   */
  isStepAllowed(stepNum, context) {
    // Steps 1-2 are shared and always allowed if previous steps completed
    if (stepNum <= 2) {
      return stepNum === 1 || context.completedSteps?.includes(stepNum - 1);
    }
    
    const migrate = context.migrate || {};
    
    switch (stepNum) {
      case 3:
        // Project Setup: requires workspace created
        return !!context.workspace?.name;
        
      case 4:
        // Import Scripts: requires project configured
        return !!migrate.projectName && !!migrate.framework;
        
      case 5:
        // Conversion Settings: requires successful import
        return migrate.importStatus === 'SUCCESS' && migrate.testCount > 0;
        
      case 6:
        // Convert: requires settings configured
        return migrate.importStatus === 'SUCCESS';
        
      case 7:
        // Run Test: requires conversion completed
        return migrate.convertStatus === 'SUCCESS' && !!migrate.runnableSuiteId;
        
      case 8:
        // Summary: requires test run completed or failed
        return context.runJob?.status === 'completed' || 
               context.runJob?.status === 'failed';
        
      default:
        return true;
    }
  },
  
  /**
   * Get step by ID
   * @param {number} stepId - Step ID
   * @returns {Object|null} Step definition
   */
  getStep(stepId) {
    return this.steps.find(s => s.id === stepId) || null;
  },
  
  /**
   * Get gate message for a step
   * Provides user-friendly message when step is gated
   * 
   * @param {number} stepNum - Step number
   * @returns {string} Gate message
   */
  getGateMessage(stepNum) {
    const messages = {
      3: 'Complete workspace setup to continue',
      4: 'Configure project details to continue',
      5: 'Import scripts successfully to continue',
      6: 'Configure conversion settings to continue',
      7: 'Complete script conversion to continue',
      8: 'Run test to view summary'
    };
    
    return messages[stepNum] || 'Complete previous steps to continue';
  }
};

export default migrateFlow;